import { useCallback, useEffect, useMemo, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import { useAuth } from '@/hooks/useAuth';
import {
  coerceSettingValue,
  fetchSettingStats,
  resolveSettings,
  setSetting,
  SETTING_DEFS,
  SETTING_LEVELS,
  settingDefMeta,
  settingLevelMeta,
  type SettingDef,
  type SettingLevel,
} from '@/lib/coreSettings';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

const GROUP_ICONS: Record<string, string> = {
  'Général': 'ri-settings-4-line',
  'Notifications': 'ri-notification-3-line',
  'Facturation': 'ri-file-list-3-line',
  'Affichage': 'ri-layout-line',
};

export default function SettingsTab({ org }: Props) {
  const { user } = useAuth();
  const [resolved, setResolved] = useState<Record<string, unknown>>({});
  const [stats, setStats] = useState({ platform: 0, country: 0, business: 0, branch: 0, user: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [level, setLevel] = useState<SettingLevel>('business');
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [r, s] = await Promise.all([
        resolveSettings({ organizationId: org.id, tenantId: org.tenant_id, userId: user?.id ?? null, country: org.country }),
        fetchSettingStats(org.id),
      ]);
      setResolved(r);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id, org.tenant_id, org.country, user?.id]);

  useEffect(() => { reload(); }, [reload]);

  const groups = useMemo(() => {
    const map: Record<string, SettingDef[]> = {};
    SETTING_DEFS.forEach((d) => {
      if (!map[d.group]) map[d.group] = [];
      map[d.group].push(d);
    });
    return map;
  }, []);

  const save = async (def: SettingDef, raw: string | boolean) => {
    setSavingKey(def.key);
    setSavedKey(null);
    try {
      const value = coerceSettingValue(def, raw);
      const payload = {
        tenantId: org.tenant_id,
        organizationId: org.id,
        level,
        settingKey: def.key,
        value,
        scopeRef: level === 'platform' || level === 'country' ? (level === 'country' ? (org.country || null) : null)
          : level === 'business' ? String(org.id)
            : level === 'branch' ? String(org.id)
              : String(user?.id ?? org.id),
        locationId: level === 'branch' ? null : null,
        userId: level === 'user' ? (user?.id ?? null) : null,
      };
      await setSetting(payload);
      setSavedKey(def.key);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Enregistrement impossible.');
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Paramètres à plusieurs niveaux</span> :
        <span className="font-medium text-foreground-600"> Plateforme → Pays → Entreprise → Établissement → Utilisateur</span>.
        Le niveau le plus précis l’emporte. Les modules résolvent leurs valeurs automatiquement via cette cascade.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      {/* Cascade */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          {SETTING_LEVELS.map((l, i, arr) => (
            <span key={l.key} className="flex items-center gap-2">
              <span className="px-3 py-1.5 rounded-full font-medium whitespace-nowrap bg-background-100 text-foreground-700 flex items-center gap-1.5">
                <i className={l.icon}></i>{l.label}
                <span className="text-foreground-400">{stats[l.key]}</span>
              </span>
              {i < arr.length - 1 && <i className="ri-arrow-right-line text-foreground-300"></i>}
            </span>
          ))}
        </div>
      </div>

      {/* Sélecteur de niveau */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-full overflow-x-auto">
        {SETTING_LEVELS.map((l) => (
          <button key={l.key} onClick={() => setLevel(l.key)} className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${level === l.key ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
            <i className={l.icon}></i>{l.label}
          </button>
        ))}
      </div>
      <p className="text-[11px] text-foreground-400 -mt-2">{settingLevelMeta(level).desc}</p>

      {loading ? (
        <div className="flex items-center justify-center py-16"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
      ) : (
        <div className="space-y-4">
          {Object.entries(groups).map(([group, defs]) => (
            <div key={group} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className={GROUP_ICONS[group] || 'ri-settings-3-line'}></i></span>
                <h4 className="text-sm font-semibold text-foreground-900">{group}</h4>
              </div>
              <div className="divide-y divide-background-200/60">
                {defs.map((def) => {
                  const effective = resolved[def.key];
                  return (
                    <div key={def.key} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="min-w-0 sm:w-64 flex items-center gap-2">
                        <i className={`${def.icon} text-foreground-500`}></i>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-800 truncate">{def.label}</p>
                          <p className="text-[11px] text-foreground-400">Effectif : <span className="font-medium text-foreground-600">{String(effective)}</span></p>
                        </div>
                      </div>
                      <div className="flex-1 flex items-center gap-2">
                        {def.type === 'boolean' ? (
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              defaultChecked={Boolean(effective)}
                              onChange={(e) => save(def, e.target.checked)}
                              className="w-4 h-4 accent-primary-500 cursor-pointer"
                            />
                            <span className="text-xs text-foreground-500">{effective ? 'Activé' : 'Désactivé'}</span>
                          </label>
                        ) : def.type === 'select' ? (
                          <select className={INPUT} defaultValue={String(effective)} onChange={(e) => save(def, e.target.value)}>
                            {(def.options || []).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        ) : (
                          <input
                            className={INPUT}
                            type={def.type === 'number' ? 'number' : 'text'}
                            defaultValue={String(effective)}
                            onBlur={(e) => { if (e.target.value !== String(effective)) save(def, e.target.value); }}
                          />
                        )}
                        {savingKey === def.key && <i className="ri-loader-4-line animate-spin text-foreground-400"></i>}
                        {savedKey === def.key && <i className="ri-checkbox-circle-line text-primary-600"></i>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}